import React, {ReactNode} from 'react';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import './button.scss';
import {trimBadgeNumber} from '../../utils/utils';

export const buttonColorTypes = [
    'primary',
    'secondary',
    'warning',
    'success',
    'error',
    'info',
    'basic',
    'gray_700',
    'gray_400',
    'gray_200',
    'gray_100',
    'primaryVariant',
    'frost',
] as const;

export type ButtonColorType = typeof buttonColorTypes[number];

export type ButtonSizeType = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface ButtonProps {
    disabled?: boolean;
    fullWidth?: boolean;
    wide?: boolean;
    children?: ReactNode;
    onClick?: (e?: React.FormEvent) => void;
    onDoubleClick?: (e?: React.FormEvent) => void;
    onMouseOver?: (e?: React.FormEvent) => void;
    onMouseOut?: (e?: React.FormEvent) => void;
    className?: string;
    target?: string;
    size?: ButtonSizeType;
    color?: ButtonColorType;
    customColor?: string;
    customIconColor?: string;
    variant?: 'solid' | 'ghost' | 'outline' | 'soft';
    leading?: string;
    trailing?: string;
    badge?: number;
    loading?: boolean;
    square?: boolean;
    padded?: boolean;
    rounded?: boolean;
    disableHover?: boolean;
    disablePointerEvents?: boolean;
    dataCy?: string;
    type?: 'button' | 'submit' | 'reset';
    align?: 'left' | 'right' | 'center' | 'stretch';
    dynamicSize?: boolean;
    fontFamily?: string;
}

export default function Button(props: ButtonProps): JSX.Element {
    const {
        onClick,
        onDoubleClick,
        onMouseOver,
        onMouseOut,
        fullWidth,
        wide,
        size = 'md',
        color = 'primary',
        variant = 'solid',
        disabled,
        leading,
        trailing,
        badge,
        loading,
        square,
        padded = true,
        rounded = false,
        disableHover,
        disablePointerEvents,
        children,
        className,
        customColor,
        customIconColor,
        dataCy,
        type = 'button',
        align = 'center',
        dynamicSize = false,
        fontFamily,
        ...rest
    } = props;

    const buttonClass = classNames(
        'c-button',
        {
            [`c-button--variant-${variant}`]: variant,
            [`c-button--size-${size}`]: size,
            [`c-button--color-${color}`]: color,
            [`c-button--fullWidth`]: fullWidth,
            [`c-button--square`]: square,
            [`c-button--padded`]: padded,
            [`c-button--rounded`]: rounded,
            [`c-button--disableHover`]: disableHover,
            [`c-button--disablePointerEvents`]: disablePointerEvents,
            [`c-button--wide`]: wide,
            [`c-button--align-${align}`]: align,
            [`c-button--dynamicSize`]: dynamicSize,
            disabled: disabled || loading,
        },
        className
    );

    let leadingNode: ReactNode = null;
    if (loading) {
        leadingNode = <Icon className="c-button__leading" icon="svg-spinners:180-ring" />;
    } else if (leading) {
        leadingNode = <Icon className="c-button__leading" icon={leading} color={customIconColor} />;
    }

    let trailingNode: ReactNode = null;
    if (trailing) {
        trailingNode = <Icon className="c-button__trailing" icon={trailing} color={customIconColor} />;
    } else if (badge) {
        trailingNode = <div className="c-button__badge">{trimBadgeNumber(badge)}</div>;
    }

    return (
        <button
            className={buttonClass}
            disabled={disabled || loading}
            onClick={disabled ? undefined : onClick}
            onDoubleClick={disabled ? undefined : onDoubleClick}
            onMouseOver={disabled ? undefined : onMouseOver}
            onMouseOut={disabled ? undefined : onMouseOut}
            style={{backgroundColor: customColor}}
            data-cy={dataCy}
            {...rest}
            type={type}
        >
            {leadingNode}
            {children && (
                <span className="c-button__content" style={{fontFamily}}>
                    {children}
                </span>
            )}
            {trailingNode}
        </button>
    );
}
